import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api, { API_URL } from '../api';
import { AuthContext } from '../context/AuthContext';
import PromoCodeInput from './PromoCodeInput';
import './ProductDetail.css';

// Book icon for Read Online button
const BookOpenIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '6px', verticalAlign: 'middle'}}>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
  </svg>
);

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redeemed, setRedeemed] = useState(false);
  const [downloadToken, setDownloadToken] = useState(null);
  const [grantingAccess, setGrantingAccess] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  
  // Check if user came from "Login to Read Online" flow (via state or query param)
  const autoReadOnline = location.state?.autoReadOnline || searchParams.get('autoReadOnline') === 'true';

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await api.get(`/api/products/${id}/`);
        setProduct(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Product not found');
        setLoading(false);
      }
    };

    const fetchReviews = async () => {
      try {
        const response = await api.get(`/api/products/${id}/reviews/`);
        setReviews(response.data.reviews || []);
        if (response.data.stats) {
          setReviewStats({
            average_rating: response.data.stats.average_rating,
            total_reviews: response.data.stats.total_reviews,
            recommendation_percentage: response.data.stats.recommend_percentage
          });
        }
      } catch (err) {
        console.error('Error fetching reviews:', err);
      }
    };

    fetchProduct();
    fetchReviews();
  }, [id]);

  // Update document title and meta tags for SEO
  useEffect(() => {
    if (product) {
      // Update page title
      document.title = `${product.title} | Christensen Kirtis Publishing`;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        const description = product.summary || product.description;
        metaDescription.setAttribute('content', description.substring(0, 160));
      }
      
      // Update Open Graph tags for social sharing
      const ogTitle = document.querySelector('meta[property="og:title"]');
      const ogDescription = document.querySelector('meta[property="og:description"]');
      const ogUrl = document.querySelector('meta[property="og:url"]');
      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      const twitterDescription = document.querySelector('meta[name="twitter:description"]');
      
      if (ogTitle) ogTitle.setAttribute('content', product.title);
      if (ogDescription) ogDescription.setAttribute('content', product.summary || product.description.substring(0, 200));
      if (ogUrl) ogUrl.setAttribute('content', `https://publishing.christensenkirtis.com/products/${product.id}`);
      if (twitterTitle) twitterTitle.setAttribute('content', product.title);
      if (twitterDescription) twitterDescription.setAttribute('content', product.summary || product.description.substring(0, 200));
    }
    
    // Cleanup: restore default title on unmount
    return () => {
      document.title = 'Christensen Kirtis Publishing';
    };
  }, [product]);

  // Auto-grant download access when user is logged in
  useEffect(() => {
    const grantDownloadAccess = async () => {
      if (!user || !product || downloadToken || grantingAccess) return;
      
      setGrantingAccess(true);
      try {
        const response = await api.post('/api/grant-download/', {
          product_id: product.id,
          email: user.email
        });
        
        if (response.data.download_token) {
          setDownloadToken(response.data.download_token);
          setRedeemed(true);
        }
      } catch (err) {
        console.error('Error granting download access:', err);
        // Don't show error to user - they can still use the promo code flow if needed
      } finally {
        setGrantingAccess(false);
      }
    };

    grantDownloadAccess();
  }, [user, product, downloadToken, grantingAccess]);

  // Auto-redirect to WebReader if user came from "Login to Read Online" flow
  useEffect(() => {
    if (autoReadOnline && downloadToken && product?.product_type === 'ebook') {
      // Clear the state so back button doesn't redirect again
      navigate(`/read/${downloadToken}`, { replace: true });
    }
  }, [autoReadOnline, downloadToken, product, navigate]);

  const getTypeLabel = (type) => {
    const labels = {
      'ebook': 'E-Book',
      'audiobook': 'Audiobook',
      'game': 'Game',
      'podcast': 'Podcast'
    };
    return labels[type] || type;
  };

  const handleSample = () => {
    if (!user) {
      navigate('/register', { state: { from: `/products/${id}`, message: 'Please register to view samples.' } });
      return;
    }
    if (product.sample_file_url) {
      // For ebooks, generate a sample EPUB instead of downloading raw file
      if (product.product_type === 'ebook') {
        window.open(`${API_URL}/api/sample-epub/${product.id}/`, '_blank');
      } else {
        window.open(product.sample_file_url, '_blank');
      }
    } else {
      alert('No sample available for this product.');
    }
  };

  const handleDownload = (outputFormat = 'original') => {
    if (downloadToken) {
      let url;
      if (outputFormat === 'epub') {
        url = `${API_URL}/api/download/${downloadToken}/?output=epub`;
      } else if (outputFormat === 'pdf') {
        url = `${API_URL}/api/download/${downloadToken}/?output=pdf`;
      } else {
        url = `${API_URL}/api/download/${downloadToken}/`;
      }
      window.open(url, '_blank');
    } else {
      alert('Download/Purchase functionality coming soon! Use a promo code below to get instant access.');
    }
  };

  const handleRedeemed = (data) => {
    setRedeemed(true);
    if (data.download_token) {
      setDownloadToken(data.download_token);
    }
  };

  const handleBack = () => {
    navigate('/products');
  };

  if (loading) {
    return (
      <div className="product-detail-container">
        <div className="loading">Loading product...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-detail-container">
        <div className="error">
          <h2>{error}</h2>
          <button onClick={handleBack} className="back-btn">Back to Products</button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-container">
      <button onClick={handleBack} className="back-btn">
        ← Back to Products
      </button>
      
      <div className="product-detail-content">
        <div className="product-detail-image">
          {product.thumbnail_url ? (
            <img src={product.thumbnail_url} alt={product.title} />
          ) : (
            <div className="product-detail-placeholder">
              <span>{getTypeLabel(product.product_type)}</span>
            </div>
          )}
        </div>

        <div className="product-detail-info">
          <span className="product-type-badge">{getTypeLabel(product.product_type)}</span>
          <h1 className="product-title">{product.title}</h1>
          
          <div className="product-price">
            {product.price > 0 ? `$${product.price}` : 'Free'}
          </div>

          <div className="product-actions">
            <button 
              onClick={handleSample} 
              className="action-btn sample-btn"
              disabled={!product.sample_file_url}
              title={!user ? 'Register to view sample' : ''}
            >
              📄 {user ? 'View Sample' : 'Register to View Sample'}
            </button>
            {/* Read Online - for ebooks */}
            {product.product_type === 'ebook' && (
              <>
                {user && redeemed && downloadToken ? (
                  <button 
                    onClick={() => navigate(`/read/${downloadToken}`)}
                    className="action-btn read-online-btn"
                    title="Read this book in your browser"
                  >
                    <BookOpenIcon /> Read Online
                  </button>
                ) : user && grantingAccess ? (
                  <button 
                    className="action-btn read-online-btn"
                    disabled={true}
                  >
                    ⏳ Preparing...
                  </button>
                ) : !user ? (
                  <button 
                    onClick={() => navigate('/login', { state: { from: `/products/${id}`, autoReadOnline: true } })}
                    className="action-btn read-online-btn login-to-read"
                  >
                    <BookOpenIcon /> Login to Read Online
                  </button>
                ) : null}
              </>
            )}
            {/* EPUB Download - enabled for logged-in users */}
            {product.product_type === 'ebook' && (
              <>
                {user && redeemed && downloadToken ? (
                  <button 
                    onClick={() => handleDownload('epub')} 
                    className="action-btn epub-btn"
                  >
                    📚 Download EPUB
                  </button>
                ) : user && grantingAccess ? (
                  <button 
                    className="action-btn epub-btn"
                    disabled={true}
                  >
                    ⏳ Preparing download...
                  </button>
                ) : !user ? (
                  <a 
                    href={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}
                    className="action-btn epub-btn login-to-download"
                  >
                    🔐 Login to Download
                  </a>
                ) : null}
              </>
            )}
            {/* PDF Download - enabled for ebooks with markdown content */}
            {product.product_type === 'ebook' && product.has_readable_content && user && redeemed && downloadToken && (
              <button 
                onClick={() => handleDownload('pdf')} 
                className="action-btn pdf-btn"
                title="Download personalized PDF"
              >
                📄 Download PDF
              </button>
            )}
          </div>

          <PromoCodeInput productId={product.id} onRedeemed={handleRedeemed} disabled={true} />

          <div className="product-section">
            <h2>Summary</h2>
            <p>{product.summary || 'No summary available.'}</p>
          </div>

          <div className="product-section">
            <h2>Description</h2>
            <p>{product.description || 'No description available.'}</p>
          </div>

          <div className="product-meta">
            <div className="meta-item">
              <strong>Added:</strong> {new Date(product.created_at).toLocaleDateString()}
            </div>
            <div className="meta-item">
              <strong>Updated:</strong> {new Date(product.updated_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="reviews-section">
        <div className="reviews-header">
          <h2>Customer Reviews</h2>
          {user && (
            <button 
              className="write-review-btn"
              onClick={() => navigate(`/products/${id}/write-review`)}
            >
              ✏️ Write a Review
            </button>
          )}
          {!user && (
            <p className="login-prompt">
              <a href="/login">Log in</a> to write a review
            </p>
          )}
        </div>
        
        {reviewStats && reviewStats.total_reviews > 0 && (
          <>
            <div className="review-stats">
              <div className="stat-item">
                <span className="stat-value">
                  {'★'.repeat(Math.round(reviewStats.average_rating))}
                  {'☆'.repeat(5 - Math.round(reviewStats.average_rating))}
                </span>
                <span className="stat-label">
                  {reviewStats.average_rating?.toFixed(1)} out of 5
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{reviewStats.total_reviews}</span>
                <span className="stat-label">
                  {reviewStats.total_reviews === 1 ? 'Review' : 'Reviews'}
                </span>
              </div>
              {reviewStats.recommendation_percentage !== null && (
                <div className="stat-item">
                  <span className="stat-value">{reviewStats.recommendation_percentage}%</span>
                  <span className="stat-label">Would Recommend</span>
                </div>
              )}
            </div>

            <div className="reviews-list">
              {reviews.map((review) => (
                <div key={review.id} className="review-card">
                  <div className="review-header">
                    <span className="review-stars">
                      {'★'.repeat(review.overall_rating)}
                      {'☆'.repeat(5 - review.overall_rating)}
                    </span>
                    <span className="review-date">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="review-text">{review.written_review}</p>
                  {review.favorite_aspects && review.favorite_aspects.length > 0 && (
                    <div className="review-aspects">
                      {review.favorite_aspects.map((aspect, i) => (
                        <span key={i} className="aspect-tag">{aspect}</span>
                      ))}
                    </div>
                  )}
                  {review.would_recommend && (
                    <span className="recommend-badge">✓ Would recommend</span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {(!reviewStats || reviewStats.total_reviews === 0) && (
          <p className="no-reviews">No reviews yet. Be the first to share your thoughts!</p>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
